import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import $ from "jquery";
import { useNavigate } from "react-router-dom";
import { Stack, Typography, Grid, Box, Skeleton } from "@mui/material";
import ReactCountryFlag from "react-country-flag";
import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import { GreenTimeRangePicker } from "../../../../common/Greens";
import { StyledMenu } from "../../../application/analytics/styled";
import { PeriodItemComponent } from "../../../application/analytics/common";
import colors from "../../../../../utils/colors";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import { DetectionFilterCategory } from "../../../../../utils/constants";
import { displayUserAgent } from "../../../../../utils/display";

import WorldVectorMap from "../../overview/components/WorldVectorMap";
import WAFDetectionInsight from "../../overview/components/WAFDetectionInsight";
import WAFDashboardOverviewStrip from "./components/WAFDashboardOverviewStrip";

import useSite from "../../../../../hooks/user/useSite";
import useAuth from "../../../../../hooks/useAuth";
import useHome from "../../../../../hooks/user/useHome";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import {
  buildWafEventFiltersFromDashboardCard,
  DASHBOARD_TOP_CARD_LIMIT,
} from "../../detection/dashboardEventNavigation";

export const PanelSkeleton = () => {
  return (
    <Grid item xs={12}>
      <Stack spacing={4}>
        {Array.from({ length: DASHBOARD_TOP_CARD_LIMIT }, (_, i) => i + 1).map((index) => (
          <Stack index={`ske_${index}`} direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={6}>
            <Skeleton
              height="22px"
              width="75%"
              py="5px"
              variant="rectangular"
              sx={{
                borderRadius: "11px",
              }}
            />
            <Skeleton
              height="22px"
              width="25%"
              py="5px"
              variant="rectangular"
              sx={{
                borderRadius: "11px",
              }}
            />
          </Stack>
        ))}
      </Stack>
    </Grid>
  );
};

export const DashboardPanelEmptyState = () => (
  <Grid item xs={12}>
    <Typography
      sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[600], py: 4, textAlign: "center" }}
    >
      No data available
    </Typography>
  </Grid>
);

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

const safeRegionName = (countryCode) => {
  const code = (countryCode || "").toString().trim().toUpperCase();
  if (!code) return "—";
  try {
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
};

const NewServicesWebApplicationDashboard = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
    setWafDashController,
  } = useAuth();
  const {
    sigs,
    mls,
    sdsigs,
    smartparses,
    regional_detections,
    top_region_detection,
    top_source_detection,
    top_path,
    top_ua,
    top_detection_type,
    top_method,
    getDashboardInfo,
  } = useSite();
  const { curProjectId } = useHome();
  const { apiSiteId: siteID } = useGlobalSite();

  const [timeRange, setTimeRange] = useState("7d");
  const [customRangeConfirm, setCustomRangeConfirm] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [customDateRange, setCustomDateRange] = useState(["2000-01-01T00:00:00", "2000-01-02T00:00:00"]);

  useEffect(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const date = String(now.getDate()).padStart(2, "0");
    const year = String(now.getFullYear());
    const _month = String(oneDayAgo.getMonth() + 1).padStart(2, "0");
    const _date = String(oneDayAgo.getDate()).padStart(2, "0");
    const _year = String(oneDayAgo.getFullYear());
    setCustomDateRange([_year + "-" + _month + "-" + _date + "T00:00:00", year + "-" + month + "-" + date + "T00:00:00"]);
  }, []);

  const getTimeRange = () => {
    if (timeRange === "custom") {
      const now = new Date();
      const timeZone =
        now.getTimezoneOffset() >= 0
          ? "-" + String(now.getTimezoneOffset() / 60).padStart(2, "0") + ":00"
          : "+" + String(now.getTimezoneOffset() / -60).padStart(2, "0") + ":00";
      return { time_zone: timeZone, from: customDateRange[0], to: customDateRange[1] };
    } else {
      return { period: timeRange };
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (timeRange === "custom" && customRangeConfirm === false) {
      return;
    }
    setCustomRangeConfirm(false);
    if (homeController) homeController.abort();
    if (wafdashController) wafdashController.abort();
    if (websiteController) websiteController.abort();
    if (wafeventController) wafeventController.abort();
    if (planController) planController.abort();
    if (siteID) {
      getDashboardInfo(setWafDashController, siteID, getTimeRange());
    }
  }, [isAuthenticated, siteID, timeRange, customDateRange, curProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectPeriod = (period) => {
    setAnchorEl(null);
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(function () {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    }
  };
  const selectCustomDateRange = (res) => {
    setCustomRangeConfirm(true);
    setCustomDateRange(res);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (timeRange === "custom" && customRangeConfirm === false) {
      return;
    }
    setCustomRangeConfirm(false);
  }, [customRangeConfirm, timeRange]);

  const handleDetail = (categoryId, data) => {
    navigate("/v2/detection/waf_event", {
      state: {
        initialFilters: buildWafEventFiltersFromDashboardCard(categoryId, data),
        timeRange: getTimeRange(),
      },
    });
  };

  const stripTimeRange = useMemo(() => {
    if (timeRange === "custom" && customRangeConfirm === false) return null;
    return getTimeRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customDateRange, customRangeConfirm]);

  return (
    <>
      <Helmet title="WAF Dashboard" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="WAF Dashboard"
          sx={{ pb: 0 }}
          actions={(
            <GreenTimeRangePicker
              selectPeriod={selectPeriod}
              selectCustomDateRange={selectCustomDateRange}
              timeRange={timeRange}
              customDateRange={customDateRange}
            />
          )}
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
        <WAFDashboardOverviewStrip
          siteId={siteID}
          timeRange={stripTimeRange}
          customDateRange={customDateRange}
        />
        <Box sx={{ width: "100%" }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Stack sx={{ height: 308, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, p: 4 }}>
                <Typography
                  sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                >
                  WAF detections by country
                </Typography>
                <WorldVectorMap regional_detections={regional_detections} />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack sx={{ height: 308, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, p: 4 }}>
                <Typography
                  sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                >
                  WAF detection insights
                </Typography>
                <WAFDetectionInsight sigs={sigs} smartparses={smartparses} />
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ width: "100%" }}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} lg={4}>
              <Stack sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2, height: "-webkit-fill-available" }}>
                <Typography
                  sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000], pl: 2, py: 2 }}
                >
                  Top WAF detections by country
                </Typography>
                <Box sx={{ width: "100%" }}>
                  <Grid container>
                    {top_region_detection === null && <PanelSkeleton />}
                    {top_region_detection !== null && top_region_detection.length === 0 && <DashboardPanelEmptyState />}
                    {top_region_detection?.slice(0, DASHBOARD_TOP_CARD_LIMIT)?.map((item, index) => (
                      <Grid key={`top_waf_detections_by_country_${index}`} item xs={12}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          spacing={3}
                          onClick={() => handleDetail(DetectionFilterCategory.TopWAFDetectionsByCountry, item?.country_iso_code)}
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            height: 42,
                            "&:hover": { backgroundColor: colors.primary["alpha"] },
                            cursor: "pointer",
                          }}
                        >
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <ReactCountryFlag
                              countryCode={item?.country_iso_code}
                              svg
                              style={{
                                width: "26px",
                                height: "18px",
                              }}
                            />
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                              {safeRegionName(item?.country_iso_code)}
                            </Typography>
                          </Stack>
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
                              {item.count}
                            </Typography>
                            <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                          </Stack>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Stack sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2, height: "-webkit-fill-available" }}>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3} sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: "21px",
                      fontWeight: "medium",
                      color: colors.neutral[1000],
                      pl: 2,
                    }}
                  >
                    Top user agent
                  </Typography>
                </Stack>
                <Box sx={{ width: "100%" }}>
                  <Grid container>
                    {top_ua === null && <PanelSkeleton />}
                    {top_ua !== null && top_ua.length === 0 && <DashboardPanelEmptyState />}
                    {top_ua?.slice(0, DASHBOARD_TOP_CARD_LIMIT)?.map((item, index) => (
                      <Grid key={`top_user_agent${index}`} item xs={12}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          spacing={3}
                          onClick={() => handleDetail(DetectionFilterCategory.TopUserAgent, item?.ua)}
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            height: 42,
                            "&:hover": { backgroundColor: colors.primary["alpha"] },
                            cursor: "pointer",
                          }}
                        >
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                color: colors.neutral[1000],
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: `calc(33vw - 180px)`,
                              }}
                            >
                              {displayUserAgent(item?.ua)}
                            </Typography>
                          </Stack>
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
                              {item.count}
                            </Typography>
                            <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                          </Stack>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Stack sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2, height: "-webkit-fill-available" }}>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3} sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: "21px",
                      fontWeight: "medium",
                      color: colors.neutral[1000],
                      pl: 2,
                    }}
                  >
                    Top paths
                  </Typography>
                </Stack>
                <Box sx={{ width: "100%" }}>
                  <Grid container>
                    {top_path === null && <PanelSkeleton />}
                    {top_path !== null && top_path.length === 0 && <DashboardPanelEmptyState />}
                    {top_path?.slice(0, DASHBOARD_TOP_CARD_LIMIT)?.map((item, index) => (
                      <Grid key={`top_user_agent${index}`} item xs={12}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          spacing={3}
                          onClick={() => handleDetail(DetectionFilterCategory.TopPath, item?.path)}
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            height: 42,
                            "&:hover": { backgroundColor: colors.primary["alpha"] },
                            cursor: "pointer",
                          }}
                        >
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                color: colors.neutral[1000],
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: `calc(33vw - 180px)`,
                              }}
                            >
                              {item.path}
                            </Typography>
                          </Stack>
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
                              {item.count}
                            </Typography>
                            <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                          </Stack>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Stack sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2, height: "-webkit-fill-available" }}>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3} sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: "21px",
                      fontWeight: "medium",
                      color: colors.neutral[1000],
                      pl: 2,
                    }}
                  >
                    Top events by source
                  </Typography>
                </Stack>
                <Box sx={{ width: "100%" }}>
                  <Grid container>
                    {top_source_detection === null && <PanelSkeleton />}
                    {top_source_detection !== null && top_source_detection.length === 0 && <DashboardPanelEmptyState />}
                    {top_source_detection?.slice(0, DASHBOARD_TOP_CARD_LIMIT)?.map((item, index) => (
                      <Grid key={`top_user_agent${index}`} item xs={12}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          onClick={() => handleDetail(DetectionFilterCategory.TopEventBySource, item?.addr)}
                          spacing={3}
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            height: 42,
                            "&:hover": { backgroundColor: colors.primary["alpha"] },
                            cursor: "pointer",
                          }}
                        >
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                color: colors.neutral[1000],
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: `calc(33vw - 180px)`,
                              }}
                            >
                              {item.addr}
                            </Typography>
                          </Stack>
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
                              {item.count}
                            </Typography>
                            <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                          </Stack>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Stack sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2, height: "-webkit-fill-available" }}>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3} sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: "21px",
                      fontWeight: "medium",
                      color: colors.neutral[1000],
                      pl: 2,
                    }}
                  >
                    Top methods
                  </Typography>
                </Stack>
                <Box sx={{ width: "100%" }}>
                  <Grid container>
                    {top_method === null && <PanelSkeleton />}
                    {top_method !== null && top_method.length === 0 && <DashboardPanelEmptyState />}
                    {top_method?.slice(0, DASHBOARD_TOP_CARD_LIMIT)?.map((item, index) => (
                      <Grid key={`top_user_agent${index}`} item xs={12}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          spacing={3}
                          onClick={() => handleDetail(DetectionFilterCategory.TopMethod, item?.method)}
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            height: 42,
                            "&:hover": { backgroundColor: colors.primary["alpha"] },
                            cursor: "pointer",
                          }}
                        >
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                color: colors.neutral[1000],
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: `calc(33vw - 180px)`,
                              }}
                            >
                              {item.method}
                            </Typography>
                          </Stack>
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
                              {item.count}
                            </Typography>
                            <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                          </Stack>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Stack sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2, height: "-webkit-fill-available" }}>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3} sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: "21px",
                      fontWeight: "medium",
                      color: colors.neutral[1000],
                      pl: 2,
                    }}
                  >
                    Top detections
                  </Typography>
                </Stack>
                <Box sx={{ width: "100%" }}>
                  <Grid container>
                    {top_detection_type === null && <PanelSkeleton />}
                    {top_detection_type !== null && top_detection_type.length === 0 && <DashboardPanelEmptyState />}
                    {top_detection_type?.slice(0, DASHBOARD_TOP_CARD_LIMIT)?.map((item, index) => (
                      <Grid key={`top_user_agent${index}`} item xs={12}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          spacing={3}
                          onClick={() => handleDetail(DetectionFilterCategory.TopDetection, item?.attack_type || item?.type)}
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            height: 42,
                            "&:hover": { backgroundColor: colors.primary["alpha"] },
                            cursor: "pointer",
                          }}
                        >
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                color: colors.neutral[1000],
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: `calc(33vw - 180px)`,
                              }}
                            >
                              {item?.attack_type || item?.type}
                            </Typography>
                          </Stack>
                          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
                              {item.count}
                            </Typography>
                            <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                          </Stack>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>
      <StyledMenu
        MenuListProps={{ "aria-labelledby": "demo-customized-button" }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <PeriodItemComponent selectPeriod={selectPeriod} />
      </StyledMenu>
    </>
  );
};
export default NewServicesWebApplicationDashboard;
